import React from 'react';
import { PageSection, SectionType } from '../types/page';
import { HeroSection } from '../components/sections/HeroSection';
import { StatsSection } from '../components/sections/StatsSection';
import { FeaturedPropertiesSection } from '../components/sections/FeaturedPropertiesSection';
import { CTASection } from '../components/sections/CTASection';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { ServicesSection } from '@/components/sections/ServicesSection';
import { BlogSection } from '@/components/sections/BlogSection';
import { GallerySection } from '@/components/sections/GallerySection';
import { ContactFormSection } from '@/components/sections/ContactFormSection';
import { FAQSection } from '@/components/sections/FAQSection';
import { TeamSection } from '@/components/sections/TeamSection';
import { CustomSection } from '@/components/sections/CustomSection';

interface SectionRendererProps {
  section: PageSection;
  className?: string;
}

/**
 * Mapea los tipos de sección a sus componentes correspondientes
 */
export const SectionRenderer: React.FC<SectionRendererProps> = ({ section, className }) => {
  // Si la sección está deshabilitada, no renderizar nada
  if (!section.enabled) {
    return null;
  }

  switch (section.type) {
    case 'HERO':
      return <HeroSection section={section} className={className} />;

    case 'STATS':
      return <StatsSection section={section} className={className} />;

    case 'FEATURED_PROPERTIES':
      return <FeaturedPropertiesSection section={section} className={className} />;

    case 'CTA':
      return <CTASection section={section} className={className} />;

    case 'TESTIMONIALS':
      return <TestimonialsSection section={section} className={className} />;

    case 'SERVICES':
      // TODO: Implementar ServicesSection cuando sea necesario
      return <ServicesSection section={section} className={className} />;

    case 'BLOG':
      // TODO: Implementar BlogSection cuando sea necesario
      return <BlogSection section={section} className={className} />;

    case 'GALLERY':
      // TODO: Implementar GallerySection cuando sea necesario
      return <GallerySection section={section} className={className} />;

    case 'CONTACT':
      // TODO: Implementar ContactFormSection cuando sea necesario
      return <ContactFormSection section={section} className={className} />;

    case 'FAQ':
      return <FAQSection section={section} className={className} />;

    case 'TEAM':
      return <TeamSection section={section} className={className} />;

    case 'CUSTOM':
      return <CustomSection section={section} className={className} />;
    default:
      console.warn(`Unknown section type: ${section.type}`);
      return <>
        <div className={`p-4 ${className}`}>
          <p>Sección no implementada: {section.type}</p>
          <pre className="bg-gray-100 p-2 rounded">
            {JSON.stringify(section, null, 2)}
          </pre>
        </div>
      </>;
  }
};

/**
 * Verifica si un tipo de sección está implementado
 */
export const isSectionTypeSupported = (type: SectionType): boolean => {
  const supportedTypes: SectionType[] = [
    'HERO',
    'STATS',
    'FEATURED_PROPERTIES',
    'CTA',
    'TESTIMONIALS',
    'SERVICES',
    'BLOG',
    'GALLERY',
    'CONTACT',
    'FAQ',
    'TEAM',
    'CUSTOM'
  ];

  return supportedTypes.includes(type);
};

/**
 * Obtiene todas las secciones habilitadas y las ordena por su campo 'order'
 */
export const getEnabledSections = (sections: PageSection[]): PageSection[] => {
  return sections
    .filter(section => section.enabled)
    .sort((a, b) => a.order - b.order);
};

/**
 * Renderiza todas las secciones de una página
 */
export const renderPageSections = (sections: PageSection[]): React.ReactElement[] => {
  const enabledSections = getEnabledSections(sections);

  return enabledSections.map((section, index) => (
    <SectionRenderer
      key={section.id}
      section={section}
      className={index === 0 ? 'first-section' : ''}
    />
  ));
};